import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import DoneAllIcon from '@markinson/uicomponents-v2/SvgIcons/DoneAll';
import { pathOr, isPrecedentFieldsFilled } from 'utils/utils';
import { HotKeys } from 'react-hotkeys';
import { HotKeyNames } from 'utils/constants';
import FocusLock from 'react-focus-lock';
import renderField from '../DataGridDevEx/CustomRenderers/RenderField';
import styles from './FilterDialog.styles';
import { IFilterDialogProperties, IFilterDialogState } from './FilterDialog.properties';
import { debounce, cloneDeep } from 'lodash';

const keyMap = {
  [HotKeyNames.Escape]: 'esc',
  [HotKeyNames.Enter]: 'enter'
};

const GroupContainer = ({ style, children }: { style: React.CSSProperties; children: React.ReactNode }) => (
  <div style={{ display: 'flex', gap: '1rem', ...style }}>
    {children}
  </div>
);

const DELAY = 500;
class FilterDialog extends React.Component<IFilterDialogProperties, IFilterDialogState> {

  handler: any = null;
  debouncedHandleApply: () => void;

  constructor(props: Readonly<IFilterDialogProperties>) {
    super(props);

    this.state = {
      apply: false,
      appliedFilters: '',
      readOnly: false,
      lookups: { data: {}, loading: {}, hasError: {}, isFocused: {} }
    };

    this.onEnter = this.onEnter.bind(this);
    this.onEscape = this.onEscape.bind(this);
    this.handler = {
      [HotKeyNames.Enter]: this.onEnter,
      [HotKeyNames.Escape]: this.onEscape,
    };

    // Debounce handleApply
    this.debouncedHandleApply = debounce(this.handleApply.bind(this), DELAY);
  }

  onEnter(): void {
    if (!this.isAnyLookupLoading() && !this.anyLookupFocused()) {
      this.applyFilter();
    }
  }
  onEscape(e: any): void {
    e.stopPropagation();
    e.preventDefault();

    const { handleClose } = this.props;
    handleClose();
    this.setState({ apply: false });
  }

  onLookupEnter = () => {
    this.applyFilter();
  }

  applyFilter = () => {
    this.setState({ apply: true });
  }

  paramsUpdated = (prevProps: Readonly<IFilterDialogProperties>): boolean => {
    return (this.props.reqParams &&
      ((Object.keys(this.props.reqParams).map((key) =>
        !prevProps.reqParams
        || this.props.reqParams[key] !== prevProps.reqParams[key])
        .includes(true))
      )
    );
  }

  isAnyLookupLoading = () => Object.values(this.state.lookups.loading).some((item) => item);
  anyLookupHasError = () => Object.values(this.state.lookups.hasError).some((item) => item);
  anyLookupFocused = () => Object.values(this.state.lookups.isFocused).some((item) => item);

  handleApply = () => {
    const { isValid, values, localUpdaterParams, onApplyFilters, formSyncErrors, touchFormFields } = this.props;
    const requiredFormSyncErrors = Object.entries(formSyncErrors);
    const requiredError = requiredFormSyncErrors.find((item) => item.includes('Required'));
    if (requiredError) {
      requiredFormSyncErrors
        .filter(([, value]) => value === 'Required')
        .forEach(([key]) => touchFormFields(this.props.formName, key));
    }

    if (isValid && !this.anyLookupHasError()) {
      if (onApplyFilters) {
        const updatedValues = cloneDeep(values);

        if (Array.isArray(localUpdaterParams) && localUpdaterParams.length > 0) {
          localUpdaterParams.forEach((key) => {
            if (this.state.lookups.data.hasOwnProperty(key)) {
              updatedValues[key] = this.state.lookups.data[key];
            }
          });
          onApplyFilters(updatedValues);
        } else {
          onApplyFilters(values);
        }
      }
    }
  }

  componentDidUpdate(prevProps: Readonly<IFilterDialogProperties>): void {
    const { reqParams } = this.props;

    const paramsUpdated = this.paramsUpdated(prevProps);
    if ((this.state.apply && !this.isAnyLookupLoading()) || paramsUpdated) {
      this.setState({ apply: false });
      this.debouncedHandleApply();
    }

    //This is the case where the data in LookupDrawer changes and FilterDialog needs update.
    if (JSON.stringify(reqParams) !== JSON.stringify(prevProps.reqParams)) {
      const { handleClose
      } = this.props;
      if (handleClose) {
        handleClose();
      }
    }
  }

  handleLookupItemChange = (fieldName: string, item: any) => {
    this.setState({
      lookups: {
        ...this.state.lookups,
        data: {
          ...this.state.lookups.data,
          [fieldName]: item
        }
      }
    });
  }

  handleLookupLoading = (fieldName: string, loading: boolean) => {
    this.setState({
      lookups: {
        ...this.state.lookups,
        loading: {
          ...this.state.lookups.loading,
          [fieldName]: loading
        }
      }
    });
  }

  handleLookupError = (fieldName: string, hasError: boolean) => {
    this.setState({
      lookups: {
        ...this.state.lookups,
        hasError: {
          ...this.state.lookups.hasError,
          [fieldName]: hasError
        }
      }
    });
  }

  handleLookupFocus = (fieldName: string, isFocused: boolean) => {
    this.setState({
      lookups: {
        ...this.state.lookups,
        isFocused: {
          ...this.state.lookups.isFocused,
          [fieldName]: isFocused
        }
      }
    });
  }

  componentDidMount(): void {
    window.addEventListener('keydown', this.handleEscape);
  }

  componentWillUnmount(): void {
    window.removeEventListener('keydown', this.handleEscape);
  }

  handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.onEscape(e);
    }
  }

  getScopedParams = (childFieldSchema: any, reqParams: any) => {
    const props = pathOr({}, ['props'], childFieldSchema ?? {});

    return ({
      ...props.isCustomerSupplierScoped ? { CustomerId: Number(reqParams.customerId) || 0, Supplier: reqParams.supplierId || '' } : {},
      ...props.isCustomerScoped ? { CustomerId: Number(reqParams.customerId) } : {},
      ...props.isWorksaleScoped ? { WorksaleID: reqParams.worksaleId } : {},
      ...props.isProductScoped ? { ProductId: Number(reqParams.productId) } : {},
      ...props.isEntityScoped ? { Entity: reqParams?.entity } : {},
      ...props.isSupplierScoped ? { Supplier: reqParams.supplierId } : {},
      ...props.isWarehouseSupplierScoped ? { Supplier: reqParams.supplierId, Warehouse: reqParams.warehouse } : {},
      ...props.isWarehouseProductScoped ? { Product: reqParams.productId, Warehouse: reqParams.warehouse } : {},
      ...props.isWarehouseProductBinScoped ? { Product: reqParams.productId, Warehouse: reqParams.warehouse, Bin: reqParams.bin } : {},
    });
  }

  getProps = (childFieldSchema: any) => (pathOr({}, ['props'], childFieldSchema ?? {}));
  getType = (childFieldSchema: any) => (pathOr({}, ['type'], childFieldSchema ?? {}));

  renderFieldElement = ({ childFieldSchema, childRowId }: { childFieldSchema: any; childRowId: any }) => {
    const { valuesSchema, operationMode, onBlur, values, initialValues, isV2, isV3 } = this.props;
    const { name } = childFieldSchema.props;
    const { readOnly } = this.state;
    const props = this.getProps(childFieldSchema);
    const type = this.getType(childFieldSchema);

    const params = this.getScopedParams(childFieldSchema, this.props.reqParams);

    const { precedentFields = [] } = props;
    const _isPrecedentFieldsFilled = isPrecedentFieldsFilled(precedentFields, values);

    let _childFieldSchema = {
      ...childFieldSchema,
      props: {
        ...props, params, disabled: !_isPrecedentFieldsFilled,
        readOnly: !_isPrecedentFieldsFilled || valuesSchema && valuesSchema[name] && valuesSchema[name]?.ReadOnly
      }
    };

    let stateAndProps: any = { ...props, valuesSchema, operationMode, onBlur, values: values || initialValues, readOnly };

    if (isV2 && props.hideInV2) { return null; }
    if (isV3 && props.hideInV3) { return null; }

    if (type === 'EX_LOOKUP_FIELD') {
      _childFieldSchema = {
        ..._childFieldSchema,
        props: {
          ..._childFieldSchema.props,
          onSelectedItemChange: (item, currentDisplay) => {
            if (props.isDisplayValue) {
              this.handleLookupItemChange(props.name, item?.Display ?? currentDisplay);
            } else {
              this.handleLookupItemChange(props.name, item);
            }
          },
          onValueChange: (value) => {
            if (props.isDisplayValue) {
              this.handleLookupItemChange(props.name, value);
            }
          },
          hasError: (loading) => {
            this.handleLookupError(props.name, loading);
          },
          isFocused: (isFocused) => {
            this.handleLookupFocus(props.name, isFocused);
          },
          onEnterKeyDown: this.onLookupEnter
        }
      };
      stateAndProps = {
        ...stateAndProps,
        isLoading: (loading) => {
          this.handleLookupLoading(props.name, loading);
        },
      };
    }

    if (type === 'PRODUCT_LOOKUP_FIELD') {
      _childFieldSchema = {
        ..._childFieldSchema,
        props: {
          ..._childFieldSchema.props,
          onSelectedItemChange: (item, currentDisplay) => {
            this.handleLookupItemChange(props.name, item?.Display ?? currentDisplay);
          },
          onTypingValueChange: (value) => {
            this.handleLookupItemChange(props.name, value);
          },
        }
      };
      stateAndProps = {
        ...stateAndProps,
      };
    }

    if (!_isPrecedentFieldsFilled) values[props.name] = null;
    const fieldData = { childFieldSchema: _childFieldSchema, childRowId };

    return renderField(fieldData, stateAndProps);

  }

  render(): React.ReactNode {
    const { classes, parameters } = this.props;

    return (
      <HotKeys keyMap={keyMap} handlers={this.handler}>
        <Paper className={classes.filterMenuPaper} >
          <FocusLock>
            <div className={classes.fieldsContainer}>
              {parameters.map((childFieldSchema: any, childRowId) => {
                if (!childFieldSchema?.group) return this.renderFieldElement({ childFieldSchema, childRowId });

                return (
                  <GroupContainer style={childFieldSchema?.style ?? {}}>
                    {
                      childFieldSchema?.group.map((childFieldGroupSchema, idx) => this.renderFieldElement({ childFieldSchema: childFieldGroupSchema, childRowId: idx }))
                    }
                  </GroupContainer>
                );
              })}
            </div>
            <div className={classes.footer}>
              <Button
                variant={'contained'}
                className={classes.applyButton}
                onClick={this.applyFilter}>
                <DoneAllIcon style={{ color: 'green' }} /> APPLY
              </Button>
            </div>
          </FocusLock>
        </Paper>
      </HotKeys>
    );
  }
}

export default withStyles(styles, { index: 1 })(FilterDialog);
